import { useEffect, useState } from 'react'
import { MessageSquare, ExternalLink, Paperclip, CheckCircle2, RotateCcw, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { formatDateTime } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

/**
 * Admin-only inbox of every in-app feedback / suggestion / issue, stored in the DB
 * so the full history (open and resolved) is visible inside ProTrack — independent of
 * GitHub. Admin can mark each item Open/Resolved.
 */
const TYPE_STYLES = {
  Bug: 'bg-red-100 text-red-700',
  Suggestion: 'bg-amber-100 text-amber-700',
  Feedback: 'bg-indigo-100 text-indigo-700',
}

export default function FeedbackInbox() {
  const { t } = useTranslation('people')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('All')   // All | Open | Resolved
  const [busyId, setBusyId] = useState(null)
  // Who a ticket may be handed to. Loaded once — it is the small set of people the capability
  // layer has already let in, not the whole user list.
  const [people, setPeople] = useState([])
  // Handing work to somebody else is a management act; doing the work is not. This endpoint
  // refuses anyone who may only WORK on tickets, and that refusal is what decides whether the
  // assignee is an editable control or just a line of text.
  const [canAssign, setCanAssign] = useState(false)

  useEffect(() => {
    api.get('/feedback/assignable')
      .then(r => { setPeople(r.data?.items || []); setCanAssign(true) })
      .catch(() => { setPeople([]); setCanAssign(false) })
  }, [])

  const assign = async (id, assigneeId) => {
    setBusyId(id)
    try {
      const fd = new FormData()
      fd.append('assignee_id', assigneeId || '')      // empty un-assigns
      await api.patch(`/feedback/${id}/assign`, fd)
      load()
    } catch (e) {
      // The backend refuses somebody who cannot open tickets, and says who — show that verbatim
      // rather than a generic failure, because the fix is a capability toggle away.
      alert(e.response?.data?.detail || 'Could not assign this ticket. Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  const load = () => {
    setLoading(true)
    const params = filter === 'All' ? {} : { status_filter: filter }
    api.get('/feedback/', { params })
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [filter])

  // The note is asked for when resolving and sent verbatim to whoever reported it. Optional,
  // because a forced field just gets "fixed" typed into it, which is worse than nothing.
  const setStatus = async (id, status, note) => {
    setBusyId(id)
    try {
      const fd = new FormData()
      fd.append('status', status)
      if (note) fd.append('resolution_note', note)
      await api.patch(`/feedback/${id}/status`, fd)
      load()
    } catch {
      alert('Could not update status. Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  // In Progress still counts as outstanding: work started is work still owed, and a count that
  // drops the moment somebody clicks "Pick up" hides the backlog from whoever manages it.
  const openCount = items.filter(i => i.status === 'Open' || i.status === 'In Progress').length

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <MessageSquare className="w-6 h-6 text-indigo-600" />
        <h1 className="text-2xl font-bold text-gray-900">{t('feedback_issues')}</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Everything submitted through the in-app feedback widget, kept here so nothing is lost.
        {filter !== 'Resolved' && openCount > 0 && <span className="ml-1 font-medium text-amber-600">{openCount} open.</span>}
      </p>

      <div className="flex gap-2 mb-5">
        {['All', 'Open', 'In Progress', 'Resolved'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm rounded-lg border ${filter === f
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-10 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-gray-400 py-16 bg-white border border-gray-100 rounded-xl">
          No feedback {filter !== 'All' ? `(${filter.toLowerCase()})` : ''} yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(fb => (
            <div key={fb.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${TYPE_STYLES[fb.type] || 'bg-gray-100 text-gray-600'}`}>{fb.type}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      fb.status === 'Resolved' ? 'bg-emerald-100 text-emerald-700'
                        : fb.status === 'In Progress' ? 'bg-blue-100 text-blue-700'
                        : fb.status === "Won't Fix" ? 'bg-gray-100 text-gray-600'
                        : 'bg-orange-100 text-orange-700'}`}>{fb.status}</span>
                    {/* The reference the reporter quotes, so a conversation about "ticket 42" can
                        be matched to a row without searching by title. */}
                    {fb.ticket_ref && <span className="text-[10px] font-mono text-gray-400">{fb.ticket_ref}</span>}
                    <h3 className="text-sm font-semibold text-gray-900">{fb.title}</h3>
                  </div>
                  {fb.description && <p className="text-sm text-gray-600 mt-1.5 whitespace-pre-wrap">{fb.description}</p>}
                  {fb.resolution_note && (
                    <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                      <span className="block text-[10px] font-semibold text-emerald-800 uppercase tracking-wide mb-0.5">
                        Sent to the reporter
                      </span>
                      <span className="text-sm text-emerald-900 whitespace-pre-line">{fb.resolution_note}</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    {fb.submitter_name || 'Someone'}{fb.submitter_role ? ` · ${fb.submitter_role}` : ''}
                    {fb.page ? ` · on ${fb.page}` : ''}
                    {fb.created_at ? ` · ${formatDateTime(fb.created_at)}` : ''}
                  </p>
                  {fb.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {fb.attachments.map((a, i) => (
                        <a key={i} href={a.url} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                          <Paperclip className="w-3 h-3" /> {a.name}
                        </a>
                      ))}
                    </div>
                  )}
                  {fb.github_issue_url && (
                    <a href={fb.github_issue_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mt-2">
                      <ExternalLink className="w-3 h-3" /> GitHub #{fb.github_issue_number}
                    </a>
                  )}
                </div>
                <div className="shrink-0">
                  {/* Who is fixing it. Sits above the status buttons because deciding WHO comes
                      before deciding WHERE it is — and because an unassigned ticket is the one
                      most likely to sit untouched. */}
                  <div className="mb-1.5">
                    {!canAssign ? (
                      <p className="text-xs text-gray-500 text-right pr-1">
                        {fb.assignee_name
                          ? <>Assigned to <span className="font-medium text-indigo-700">{fb.assignee_name}</span></>
                          : 'Unassigned'}
                      </p>
                    ) : (
                    <select
                      value={fb.assignee_id || ''}
                      onChange={e => assign(fb.id, e.target.value)}
                      disabled={busyId === fb.id}
                      title="Who is working on this"
                      className={`w-full text-xs rounded-lg border px-2 py-1.5 disabled:opacity-50 ${
                        fb.assignee_id
                          ? 'border-indigo-200 bg-indigo-50 text-indigo-800'
                          : 'border-gray-200 bg-white text-gray-500'}`}
                    >
                      <option value="">Unassigned</option>
                      {people.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    )}
                  </div>
                  {fb.status !== 'Resolved' && fb.status !== "Won't Fix" ? (
                    <div className="flex flex-col gap-1.5">
                      {/* So the reporter can tell "somebody has this" from "nobody read it". */}
                      {fb.status === 'Open' && (
                        <button
                          onClick={() => setStatus(fb.id, 'In Progress')}
                          disabled={busyId === fb.id}
                          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg px-3 py-1.5 disabled:opacity-50"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Pick up
                        </button>
                      )}
                      <button
                        onClick={() => {
                          // Asked here because this is the only moment anybody knows what was
                          // actually done. Cancel leaves the ticket untouched.
                          const note = window.prompt(
                            `Resolving ${fb.ticket_ref || 'this ticket'}. What was done? ` +
                            `(sent to ${fb.submitter_name || 'the reporter'} \u2014 leave blank to skip)`)
                          if (note === null) return
                          setStatus(fb.id, 'Resolved', note.trim())
                        }}
                        disabled={busyId === fb.id}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-3 py-1.5 disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setStatus(fb.id, 'Open')}
                      disabled={busyId === fb.id}
                      className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 rounded-lg px-3 py-1.5 disabled:opacity-50"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Reopen
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
